import { useLocation } from 'react-router-dom'

/**
 * Privacy Notice — required by REFEDS Code of Conduct v2 (CoCo v2),
 * which our SP descriptor at auth.polygraf.app declares. Federation IdPs
 * link to this page from their consent screens; CESNET reviews it during
 * MetaMan registration.
 *
 * Reachable at /privacy (English) and /cs/privacy (Czech). The path is
 * referenced from `mdui:PrivacyStatementURL` in
 * docker/simplesaml/config/authsources.php.
 *
 * Keep it terse and accurate — overpromising violates GDPR Article 5(a)
 * ("lawfulness, fairness and transparency"); under-disclosing violates
 * the CoCo v2 attribute-disclosure rules.
 */
export function PrivacyPage() {
	const location = useLocation()
	const isCzech = location.pathname.startsWith('/cs/')

	return isCzech ? <PrivacyCs /> : <PrivacyEn />
}

function PrivacyEn() {
	return (
		<article className="prose prose-slate mx-auto max-w-2xl px-6 py-12">
			<h1>Privacy Notice — Polygraf Online</h1>
			<p className="text-sm opacity-70">
				Last updated: 2026-05-06 · <a href="/cs/privacy">Česky</a>
			</p>

			<h2>Who runs the service</h2>
			<p>
				Polygraf Online is operated by Středisko Teiresiás, Masaryk University (Masarykova
				univerzita, Žerotínovo nám. 617/9, 601 77 Brno, IČO 00216224). Day-to-day operations are
				handled by Blaze; technical and Sirtfi security contact: Daniel Suchan (
				<code>suchan@blaze.codes</code>).
			</p>

			<h2>What we collect when you sign in via eduID.cz</h2>
			<p>
				When you sign in through the Czech academic federation eduID.cz, your home institution
				releases the following attributes about you to us:
			</p>
			<ul>
				<li>
					<code>eduPersonPrincipalName</code> — your federation-wide unique identifier (e.g.{' '}
					<code>123456@muni.cz</code>). Used as your account ID.
				</li>
				<li>
					<code>mail</code> — your institutional email address. Used to send you service
					notifications.
				</li>
				<li>
					<code>givenName</code>, <code>sn</code>, <code>displayName</code> — your name. Used to
					address you in the UI.
				</li>
				<li>
					<code>eduPersonScopedAffiliation</code> — your role at your institution (e.g.{' '}
					<code>student@muni.cz</code>). Used to grant role-appropriate access.
				</li>
			</ul>
			<p>
				The bundle above is the REFEDS Research and Scholarship attribute set; your home institution
				releases it to any service that declares the R&S entity category (which we do).
			</p>

			<h2>What we collect when you use the service</h2>
			<ul>
				<li>
					Real-time speech transcripts that you (or a writer) produce, plus presentation media you
					stream — only for the duration of the live session and any retained recordings you
					explicitly create.
				</li>
				<li>Connection metadata (IP, user agent) for short-term abuse mitigation.</li>
				<li>
					Standard server logs (request paths, timestamps, response codes) for operational
					debugging. Retained for 30 days.
				</li>
			</ul>

			<h2>What we do NOT do</h2>
			<ul>
				<li>We do not sell or share your attributes with third parties.</li>
				<li>
					We do not use your data for advertising, profiling, or any commercial purpose unrelated to
					providing the service.
				</li>
				<li>
					We do not transfer your data outside the European Economic Area (EEA). All processing
					happens on infrastructure in the EU.
				</li>
			</ul>

			<h2>How long we keep your data</h2>
			<ul>
				<li>
					Account record: until you delete your account, or 12 months of inactivity, whichever comes
					first.
				</li>
				<li>Live transcript data: only for the duration of the session.</li>
				<li>
					Recorded session data: for as long as you choose to keep it; you can delete recordings at
					any time from your dashboard.
				</li>
				<li>Server logs: 30 days.</li>
			</ul>

			<h2>Your rights under GDPR</h2>
			<p>
				You have the right to access, rectify, export, and erase the personal data we hold about
				you, and the right to object to processing. To exercise any of these rights, email{' '}
				<code>suchan@blaze.codes</code> from the address associated with your account; we will
				respond within 30 days.
			</p>
			<p>
				If you believe we are processing your data unlawfully, you have the right to complain to the
				Czech Office for Personal Data Protection (Úřad pro ochranu osobních údajů),{' '}
				<code>posta@uoou.cz</code>.
			</p>

			<h2>Security incidents</h2>
			<p>
				We are committed to the REFEDS Sirtfi v1 framework. If you believe a security incident has
				affected our service, contact the Sirtfi security contact at <code>suchan@blaze.codes</code>
				.
			</p>

			<h2>Changes to this notice</h2>
			<p>
				We will publish material changes here at least 14 days before they take effect, and notify
				federation IdPs via metadata updates. The current version is always the one published at{' '}
				<a href="https://polygraf.app/privacy">https://polygraf.app/privacy</a>.
			</p>
		</article>
	)
}

function PrivacyCs() {
	return (
		<article className="prose prose-slate mx-auto max-w-2xl px-6 py-12">
			<h1>Zásady ochrany soukromí — Polygraf Online</h1>
			<p className="text-sm opacity-70">
				Aktualizováno: 2026-05-06 · <a href="/privacy">English</a>
			</p>

			<h2>Provozovatel služby</h2>
			<p>
				Polygraf Online provozuje Středisko Teiresiás Masarykovy univerzity (Masarykova univerzita,
				Žerotínovo nám. 617/9, 601 77 Brno, IČO 00216224). Provoz zajišťuje firma Blaze; technický a
				Sirtfi bezpečnostní kontakt: Daniel Suchan (<code>suchan@blaze.codes</code>).
			</p>

			<h2>Atributy, které získáváme po přihlášení přes eduID.cz</h2>
			<p>
				Při přihlášení přes českou akademickou federaci eduID.cz nám vaše domovská instituce předá
				tyto atributy:
			</p>
			<ul>
				<li>
					<code>eduPersonPrincipalName</code> — váš stabilní federační identifikátor (např.{' '}
					<code>123456@muni.cz</code>). Používá se jako ID účtu.
				</li>
				<li>
					<code>mail</code> — vaše institucionální e-mailová adresa. Používá se pro provozní
					notifikace.
				</li>
				<li>
					<code>givenName</code>, <code>sn</code>, <code>displayName</code> — vaše jméno. Používá se
					k oslovení v rozhraní.
				</li>
				<li>
					<code>eduPersonScopedAffiliation</code> — vaše role na instituci (např.{' '}
					<code>student@muni.cz</code>). Používá se pro přidělení odpovídajících oprávnění.
				</li>
			</ul>
			<p>
				Tento balík odpovídá kategorii REFEDS Research and Scholarship; vaše domovská instituce ho
				automaticky uvolní službám, které kategorii R&S deklarují (což my děláme).
			</p>

			<h2>Co získáváme při samotném používání služby</h2>
			<ul>
				<li>
					Živý přepis řeči, který vy (nebo přepisovatel) vytvoříte, plus prezentační média, která
					streamujete — jen po dobu živé relace a případně tak dlouho, jak si výslovně necháte
					záznam uchovat.
				</li>
				<li>Metadata spojení (IP, user agent) pro krátkodobou ochranu před zneužitím.</li>
				<li>
					Standardní serverové logy (cesty požadavků, časové razítka, návratové kódy) pro provozní
					ladění. Uchováváme 30 dní.
				</li>
			</ul>

			<h2>Co NEDĚLÁME</h2>
			<ul>
				<li>Vaše atributy neprodáváme ani nesdílíme s třetími stranami.</li>
				<li>
					Nepoužíváme vaše údaje pro reklamu, profilování nebo jakýkoliv komerční účel mimo
					poskytování služby.
				</li>
				<li>
					Vaše údaje nepřenášíme mimo Evropský hospodářský prostor (EHP). Veškeré zpracování probíhá
					na infrastruktuře v EU.
				</li>
			</ul>

			<h2>Doba uchování</h2>
			<ul>
				<li>Záznam o účtu: dokud účet nesmažete, nebo po 12 měsících neaktivity.</li>
				<li>Data živého přepisu: jen po dobu trvání relace.</li>
				<li>
					Data uložených relací: jak dlouho si necháte; nahrávky můžete kdykoliv smazat z přehledu.
				</li>
				<li>Serverové logy: 30 dní.</li>
			</ul>

			<h2>Vaše práva podle GDPR</h2>
			<p>
				Máte právo na přístup k osobním údajům, jejich opravu, přenositelnost a výmaz, a také právo
				vznést námitku proti zpracování. Pro uplatnění kteréhokoli z těchto práv pište na{' '}
				<code>suchan@blaze.codes</code> z adresy spojené s vaším účtem; odpovíme do 30 dní.
			</p>
			<p>
				Pokud se domníváte, že zpracováváme vaše údaje protiprávně, máte právo podat stížnost u
				Úřadu pro ochranu osobních údajů, <code>posta@uoou.cz</code>.
			</p>

			<h2>Bezpečnostní incidenty</h2>
			<p>
				Hlásíme se k rámci REFEDS Sirtfi v1. Pokud se domníváte, že došlo k bezpečnostnímu incidentu
				týkajícímu se naší služby, kontaktujte bezpečnostního kontaktu Sirtfi na{' '}
				<code>suchan@blaze.codes</code>.
			</p>

			<h2>Změny těchto zásad</h2>
			<p>
				Podstatné změny zveřejníme alespoň 14 dní před jejich účinností a upozorníme federační IdP
				prostřednictvím aktualizace metadat. Aktuální verze je vždy ta na{' '}
				<a href="https://polygraf.app/cs/privacy">https://polygraf.app/cs/privacy</a>.
			</p>
		</article>
	)
}

export default PrivacyPage
